import { create } from 'zustand';
import { Ban, BanType } from '../types/ban';
import { collection, doc,getDocs ,setDoc, deleteDoc, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

type BansState = {
  bans: Record<string, Ban>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initializeListener: () => () => void;
  createBan: (ban: Omit<Ban, 'id'>) => Promise<void>;
  updateBan: (banId: string, updates: Partial<Ban>) => Promise<void>;
  removeBan: (banId: string) => Promise<void>;
  checkUserBans: (userId: string, banType: BanType) => Promise<boolean>;
};

export const useBansStore = create<BansState>((set, get) => ({
  bans: {},
  loading: false,
  error: null,
  initialized: false,

  initializeListener: () => {
    if (get().initialized) return () => {};
    
    const unsubscribe = onSnapshot(
      collection(firestore, 'Bans'),
      (snapshot) => {
        const bans: Record<string, Ban> = {};
        snapshot.forEach((doc) => {
          bans[doc.id] = {
            ...doc.data(),
            id: doc.id
          } as Ban;
        });
        set({ bans, initialized: true });
      },
      (error) => {
        console.error('Error in bans listener:', error);
        set({ error: error.message, initialized: false });
      }
    );

    return unsubscribe;
  },

  createBan: async (banData) => {
    set({ loading: true, error: null });
    try {
      const banRef = doc(collection(firestore, 'Bans'));
      const ban: Ban = {
        ...banData,
        id: banRef.id,
        isActive: true
      };
      
      await setDoc(banRef, ban);
      
      set((state) => ({
        bans: {
          ...state.bans,
          [ban.id]: ban
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error creating ban:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateBan: async (banId, updates) => {
    set({ loading: true, error: null });
    try {
      await updateDoc(doc(firestore, 'Bans', banId), updates);
      
      set((state) => ({
        bans: {
          ...state.bans,
          [banId]: {
            ...state.bans[banId],
            ...updates
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating ban:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  removeBan: async (banId) => {
    set({ loading: true, error: null });
    try {
      await deleteDoc(doc(firestore, 'Bans', banId));
      
      set((state) => {
        const newBans = { ...state.bans };
        delete newBans[banId];
        return { bans: newBans, loading: false };
      });
    } catch (error: any) {
      console.error('Error removing ban:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  checkUserBans: async (userId, banType) => {
    try {
      const q = query(
        collection(firestore, 'Bans'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const now = Date.now();
      
      return snapshot.docs.some(doc => {
        const ban = doc.data() as Ban;
        const isExpired = ban.expiresAt !== null && ban.expiresAt < now;
        return !isExpired && (ban.banType === banType || ban.banType === 'full');
      });
    } catch (error) {
      console.error('Error checking user bans:', error);
      return false;
    }
  }
}));